import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IABTestConfiguration, IABTestEngine, ICreateABTestArbitrationFormValues } from '~/types/arbitration'

import EditModalContent from './EditModal'
import HistoryModalContent from './HistoryModal'

import ABTestPlacementCard from '../components/ABTestPlacementCard'
import ActionHeader from '../components/ActionHeader'

import { updateABTestEngine } from '~/api/arbitration'
import Layout from '~/components/Layouts/Layout'
import Modal from '~/components/Modal'
import { DEFAULT_ITEMS_PER_PAGE_VALUE } from '~/const'
import { appsGoneToArbitrationV2 } from '~/const/arbitration'
import { arbitrationKeys, useEngineQuery } from '~/queries/arbitration'
import { useNavigationGraphsListQuery } from '~/queries/navigation-graphs'
import { usePlacementsQuery } from '~/queries/placements'
import { useGetMeAsUserQuery } from '~/queries/users'
import { ENGINE_TYPE } from '~/types/arbitration'
import { displayToast } from '~/utils/toast'

import './styles.scss'

export const MODAL_ID = 'edit-pool-modal'

interface Props {
  selectedApp: IAppSummary
}

const ABTestEngine = ({ selectedApp }: Props): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { data: user } = useGetMeAsUserQuery()

  const [selectedConfiguration, setSelectedConfiguration] = useState<IABTestConfiguration>()

  const [selectedHistory, setSelectedHistory] = useState<IABTestConfiguration>()

  const [search, setSearch] = useState('')

  const { mutateAsync: updateABTestEngineMutation } = useMutation({ mutationFn: updateABTestEngine })

  const { data: engine } = useEngineQuery({
    appKey: selectedApp.app_key,
    deviceType: selectedApp.device_type,
    engineType: ENGINE_TYPE.AB_TEST,
  })

  const { data: placementsData } = usePlacementsQuery({
    appKey: selectedApp.app_key,
    size: DEFAULT_ITEMS_PER_PAGE_VALUE,
  })

  const { data: navigationGraphs } = useNavigationGraphsListQuery(
    {
      'app-key': selectedApp.app_key,
      'device-type': selectedApp.device_type,
      size: DEFAULT_ITEMS_PER_PAGE_VALUE,
    },
    { enabled: !!selectedApp }
  )

  const placementOptions = placementsData?.data && placementsData.data.map(({ key }) => ({ label: key, value: key }))

  const navigationGraphOptions =
    (navigationGraphs?.data && navigationGraphs.data.map(({ title, uuid }) => ({ label: title, value: uuid }))) || []

  const placements =
    engine &&
    Object.entries(engine as IABTestEngine).map(([placement, variations]) => ({
      placement,
      variations,
    }))

  const isSelectedAppGoneToArbitrationV2 =
    selectedApp &&
    appsGoneToArbitrationV2.some(
      ({ app_key, device_type }) => app_key === selectedApp.app_key && device_type === selectedApp.device_type
    )

  /** Handlers */

  const closeEditModal = () => setSelectedConfiguration(undefined)

  const closeHistoryModal = () => setSelectedHistory(undefined)

  const handleSubmit = async (values: ICreateABTestArbitrationFormValues) => {
    try {
      const { controlVariation, testedVariations } = values

      if (controlVariation) {
        await updateABTestEngineMutation({
          appKey: selectedApp.app_key,
          composition: {
            control_variation: {
              id: controlVariation.id?.value || '',
              traffic_share: controlVariation.trafficShare,
            },
            tested_variations:
              testedVariations?.map(({ id, trafficShare }) => ({
                id: id?.value || '',
                traffic_share: trafficShare,
              })) || [],
          },
          deviceType: selectedApp.device_type,
          placement: values.placement.value,
          updatedBy: user?.user_id,
        })
      }

      setSelectedConfiguration(undefined)
      displayToast('Engine successfully updated', 'success')
      queryClient.invalidateQueries({
        queryKey: arbitrationKeys.engine(ENGINE_TYPE.AB_TEST, selectedApp.app_key, selectedApp.device_type),
      })
    } catch {
      displayToast('Engine could not be updated', 'error')
    }
  }

  const handleSearch = (value: string) => {
    setSearch(value)
  }

  return (
    <Layout className="ab-test-engine-view">
      {isSelectedAppGoneToArbitrationV2 ? (
        <span className="gone-to-arbitration-v2-caption">
          This application uses the{' '}
          <a
            className="gone-to-arbitration-v2-caption__link"
            href="https://dashboard-dot-mwm-analytics.appspot.com/ds-arbitration"
            rel="noreferrer"
            target="_blank"
          >
            Arbitration V2
          </a>{' '}
          available in the AB Test Manager. It is now managed by the MWM Platform team.
        </span>
      ) : (
        <>
          {engine ? <ActionHeader onSearch={handleSearch} /> : null}
          {placements && placementOptions && navigationGraphOptions && engine ? (
            <div className="engine-placements-container">
              {placements.map(({ placement, variations }, index) =>
                placement.includes(search) ? (
                  <div className="engine-placements-container__row" key={index}>
                    <ABTestPlacementCard
                      navigationGraphOptions={navigationGraphOptions}
                      onEdit={() => {
                        setSelectedConfiguration({ composition: variations.composition, placement })
                      }}
                      onHistoryClick={() => {
                        setSelectedHistory({ composition: variations.composition, placement })
                      }}
                      placementId={placement}
                      placementOptions={placementOptions}
                      variations={variations}
                    />
                  </div>
                ) : null
              )}
            </div>
          ) : null}
          <Modal
            className="edit-pool-modal"
            id={MODAL_ID}
            isOpen={!!selectedConfiguration}
            onCloseModal={closeEditModal}
          >
            {selectedConfiguration ? (
              <EditModalContent
                configuration={selectedConfiguration}
                modalId={MODAL_ID}
                navigationGraphOptions={navigationGraphOptions}
                onClose={closeEditModal}
                onSubmit={handleSubmit}
              />
            ) : null}
          </Modal>
          <Modal className="history-modal" isOpen={!!selectedHistory} onCloseModal={closeHistoryModal}>
            {selectedHistory ? (
              <HistoryModalContent
                appKey={selectedApp.app_key}
                deviceType={selectedApp.device_type}
                navigationGraphOptions={navigationGraphOptions}
                placementId={selectedHistory.placement}
              />
            ) : null}
          </Modal>
        </>
      )}
    </Layout>
  )
}

export default ABTestEngine
